import React from "react";
import { useField, useFormalizrContext } from "@2po/formalizr";
import { Input } from "@2po-dpam/components";
import * as style from "./style.module.scss";

import { useContentfulFormalizr } from "@hooks";
import { type FormalizrContext } from "@types";
import { fillDynamicsForm } from "@utils/dynamics";
import { validateEmail } from "@utils/formalizr";
import { FormGroup, Label } from "reactstrap";

type Props = {
    name: string;
    type: string;
    [key: string]: string;
};

const FormalizerInputField = ({ name, type, ...props }: Props) => {
    const {
        formName,
        blacklistDomains,
        locale: contextLocale,
    } = useContentfulFormalizr();

    const isCheckbox = type === "checkbox";

    const [fieldProps, fieldMeta, locale] = useField({
        name,
        validate: value => {
            if (type === "email") {
                const { status, error } = validateEmail(
                    value,
                    blacklistDomains,
                );
                if (status === "error") {
                    return error;
                }
            }
            fillDynamicsForm(formName, fieldProps.name, value);
        },
    });

    const options = fieldMeta.widget?.options ?? {};
    const context: FormalizrContext = useFormalizrContext() ?? {
        translations: {},
        errors: {},
    };

    const placeholder = context?.translations
        ? context.translations[locale]?.[
              options["validations"]?.placeholder_copy?.i18n
          ]
        : "";

    const getFieldValidity = () => {
        if (fieldMeta.touched) {
            if (fieldMeta.error) {
                return true;
            }
            if (fieldMeta.value && !fieldMeta.error) {
                return false;
            }
        }
    };

    const showErrorMessage = () => fieldMeta.error && fieldMeta.touched;

    return (
        <FormGroup>
            {!isCheckbox && !options?.hideLabel && (
                <Label className={style.label} htmlFor={fieldProps.name}>
                    {fieldProps.label[locale]}
                </Label>
            )}
            <Input
                id={fieldProps.name}
                invalid={getFieldValidity()}
                locale={contextLocale}
                name={fieldProps.name}
                onBlur={fieldProps.onBlur}
                onChange={fieldProps.onChange}
                placeholder={placeholder}
                type={type}
                {...props}
            />
            {isCheckbox && !options?.hideLabel && (
                <Label check className={style.label} htmlFor={fieldProps.name}>
                    {fieldProps.label[locale]}
                </Label>
            )}
            {showErrorMessage() && (
                <p className={style.error}>{fieldMeta.error}</p>
            )}
        </FormGroup>
    );
};

export default FormalizerInputField;
